import { withErrorBoundary } from 'app/components/error-boundary';
import { Column, Row } from 'app/design/primitives/box';
import { Page } from 'app/design/primitives/page';
import { ListPage } from 'app/design/primitives/page/list';
import { Text } from 'app/design/primitives/text';
import { Thumbnail } from 'app/design/primitives/thumbnail';

import { useNavigationOptions } from 'app/hooks/navigation';
import { localeAwareFormatDistance } from 'app/lib/date';
import { useCreatePostComment, usePost, usePostComments } from 'app/network/post';
import type { NavigationScreen } from 'app/types/navigation';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ActivityIndicator, Pressable, StyleSheet, TextInput, View } from 'react-native';
import { createTheme as createDSTheme } from 'app/shopify';
import { IconMessageCircle } from '@tabler/icons-react-native';
import { ICON_STROKE_WIDTH } from 'app/utils/constants';
import { Accessory } from 'app/design/primitives/page/accessory';
import { useSocialFeedFeature } from 'app/hooks/use-social-feed-feature';
import Toast from 'react-native-toast-message';
import { PostCommentsSendButtonContent } from './post-comments-send-button-content';
const theme = createDSTheme();

type PostCommentsStatus = 'hidden' | 'loading' | 'error' | 'missing';

function getPostCommentsStatus({
  areCommentsLoading,
  canUseSocialFeed,
  hasCommentsError,
  isPostLoading,
  isSocialFeedLoading,
  postExists,
}: {
  areCommentsLoading: boolean;
  canUseSocialFeed: boolean;
  hasCommentsError: boolean;
  isPostLoading: boolean;
  isSocialFeedLoading: boolean;
  postExists: boolean;
}): PostCommentsStatus | null {
  if (isSocialFeedLoading || !canUseSocialFeed) return 'hidden';
  if (isPostLoading || areCommentsLoading) return 'loading';
  if (hasCommentsError) return 'error';
  if (!postExists) return 'missing';
  return null;
}

function PostCommentsStatusView({
  onRefetchComments,
  onRefetchPost,
  status,
}: {
  onRefetchComments: () => void;
  onRefetchPost: () => void;
  status: PostCommentsStatus;
}) {
  const { t } = useTranslation('posts');

  if (status === 'hidden') return null;

  if (status === 'loading') {
    return (
      <Page>
        <Column alignItems="center" flex={1} justifyContent="center">
          <ActivityIndicator color={theme.colors['icon.secondary']} />
        </Column>
      </Page>
    );
  }

  const retry = () => {
    if (status === 'missing') onRefetchPost();
    onRefetchComments();
  };

  return (
    <Page>
      <Column
        alignItems="center"
        flex={1}
        gap="spacing.general.x2"
        justifyContent="center"
        padding="spacing.general.x12"
      >
        {status === 'missing' ? (
          <Text color="text.tertiary" textAlign="center">
            {t('comments.empty')}
          </Text>
        ) : null}
        <Text color="text.secondary" textAlign="center">
          {t('comments.loadError')}
        </Text>
        <Text color="text.primary" fontWeight="700" onPress={retry}>
          {t('common:retry', { defaultValue: 'Retry' })}
        </Text>
      </Column>
    </Page>
  );
}

const PostCommentsScreenComponent: NavigationScreen<'post-comments'> = ({ navigation, route }) => {
  const { postId } = route.params;
  const { t } = useTranslation('posts');
  const { canUseSocialFeed, isSocialFeedLoading } = useSocialFeedFeature();
  const [newComment, setNewComment] = useState('');
  const [isPostExpanded, setIsPostExpanded] = useState(false);

  useNavigationOptions(navigation, {
    headerTitle: () => (
      <Text variant="bodyXLargeMedium" color="white-label.header.on-header">
        {t('comments.title')}
      </Text>
    ),
  });

  const { data: post, isLoading: isPostLoading, refetch: refetchPost } = usePost(postId, { enabled: canUseSocialFeed });
  const {
    data: comments = [],
    isLoading: areCommentsLoading,
    isError: hasCommentsError,
    refetch: refetchComments,
  } = usePostComments(postId, { enabled: canUseSocialFeed });
  const createComment = useCreatePostComment(postId);

  React.useEffect(() => {
    if (!createComment.error) return;

    Toast.show({
      type: 'error',
      text1: t('comments.submitError'),
      text2: createComment.error.message,
    });
  }, [createComment.error, t]);

  React.useEffect(() => {
    if (!isSocialFeedLoading && !canUseSocialFeed) navigation.goBack();
  }, [canUseSocialFeed, isSocialFeedLoading, navigation]);

  const handleSubmit = () => {
    const body = newComment.trim();
    if (!canUseSocialFeed || !body || createComment.isPending) return;
    createComment.mutate(body, { onSuccess: () => setNewComment('') });
  };

  const isCommentButtonDisabled = !newComment.trim() || createComment.isPending;
  const hasCommentText = Boolean(newComment.trim());

  const screenStatus = getPostCommentsStatus({
    areCommentsLoading,
    canUseSocialFeed,
    hasCommentsError,
    isPostLoading,
    isSocialFeedLoading,
    postExists: Boolean(post),
  });
  if (screenStatus || !post) {
    return (
      <PostCommentsStatusView
        onRefetchComments={() => void refetchComments()}
        onRefetchPost={() => void refetchPost()}
        status={screenStatus ?? 'missing'}
      />
    );
  }

  return (
    <ListPage
      accessory={
        <Accessory>
          <Row alignItems="flex-end" gap="spacing.general.x2" style={styles.composer}>
            <TextInput
              maxLength={1000}
              multiline
              onChangeText={setNewComment}
              placeholder={t('comments.placeholder')}
              placeholderTextColor={theme.colors['text.tertiary']}
              style={styles.composerInput}
              textAlignVertical="center"
              value={newComment}
            />
            <Pressable
              accessibilityLabel={t('comments.send')}
              accessibilityRole="button"
              disabled={!newComment.trim() || createComment.isPending}
              hitSlop={6}
              onPress={handleSubmit}
              style={[styles.sendButton, isCommentButtonDisabled && styles.sendButtonDisabled]}
            >
              <PostCommentsSendButtonContent hasCommentText={hasCommentText} isPending={createComment.isPending} />
            </Pressable>
          </Row>
        </Accessory>
      }
      contentContainerStyle={styles.list}
      data={comments}
      ItemSeparatorComponent={() => <View style={{ height: theme.spacing['spacing.general.x4'] }} />}
      keyExtractor={(item) => item.id}
      ListHeaderComponent={
        <Column gap="spacing.general.x4">
          <Column gap="spacing.general.x2" style={styles.postContext}>
            <Row alignItems="center" gap="spacing.general.x2">
              <Thumbnail height={36} source={{ uri: post.user.profile?.picture ?? undefined }} />
              <Column flex={1}>
                <Text fontSize={12} fontWeight="700">
                  {t('comments.postBy', { name: post.user.profile?.firstName ?? '-' })}
                </Text>
                <Text color="text.tertiary" fontSize={11}>
                  {t('comments.aboutItem', { item: post.loan.listing.item.name })} ·{' '}
                  {t('feed.timeAgo', { time: localeAwareFormatDistance(new Date(), new Date(post.createdAt)) })}
                </Text>
              </Column>
            </Row>
            <Text color="text.secondary" numberOfLines={isPostExpanded ? undefined : 3} style={styles.postText}>
              {post.text}
            </Text>
            {post.text.length > 180 ? (
              <Pressable
                accessibilityRole="button"
                onPress={() => setIsPostExpanded((expanded) => !expanded)}
                style={styles.expandPostButton}
              >
                <Text color="text.interactive" fontSize={12} fontWeight="700">
                  {isPostExpanded ? t('comments.showLess') : t('comments.showPost')}
                </Text>
              </Pressable>
            ) : null}
          </Column>
          <Row alignItems="center" justifyContent="space-between" style={styles.commentsHeading}>
            <Text fontWeight="700" variant="bodyLarge">
              {t('comments.count', { count: comments.length })}
            </Text>
            {comments.length === 0 ? (
              <Text color="text.tertiary" fontSize={12}>
                {t('comments.publicHint')}
              </Text>
            ) : null}
          </Row>
        </Column>
      }
      ListHeaderComponentStyle={styles.headerSpacing}
      ListEmptyComponent={
        <Column alignItems="center" gap="spacing.general.x3" padding="spacing.general.x12">
          <Column alignItems="center" justifyContent="center" style={styles.emptyIcon}>
            <IconMessageCircle color={theme.colors['icon.secondary']} size={28} strokeWidth={ICON_STROKE_WIDTH} />
          </Column>
          <Text fontWeight="700" textAlign="center" variant="bodyLarge">
            {t('comments.emptyTitle')}
          </Text>
          <Text color="text.secondary" textAlign="center">
            {t('comments.empty')}
          </Text>
        </Column>
      }
      renderItem={({ item }) => (
        <Row alignItems="flex-start" gap="spacing.general.x2">
          <Thumbnail height={38} source={{ uri: item.user.profile?.picture ?? undefined }} />
          <Column flex={1} gap="spacing.general.x1" style={styles.comment}>
            <Row alignItems="center" flexWrap="wrap" gap="spacing.general.x2">
              <Text fontWeight="700">{item.user.profile?.firstName ?? '-'}</Text>
              <Text color="text.tertiary" fontSize={11}>
                {localeAwareFormatDistance(new Date(), new Date(item.createdAt))}
              </Text>
            </Row>
            <Text style={styles.commentBody}>{item.body}</Text>
          </Column>
        </Row>
      )}
    />
  );
};

export const PostCommentsScreen: NavigationScreen<'post-comments'> = withErrorBoundary(PostCommentsScreenComponent);

const styles = StyleSheet.create({
  list: {
    paddingBottom: theme.spacing['spacing.general.x4'],
  },
  headerSpacing: {
    paddingBottom: theme.spacing['spacing.general.x5'],
  },
  postContext: {
    backgroundColor: theme.colors['surface.secondary-neutral'],
    borderRadius: theme.borderRadii['radius.lg'],
    padding: theme.spacing['spacing.general.x3'],
  },
  postText: {
    lineHeight: 21,
  },
  expandPostButton: {
    alignSelf: 'flex-start',
  },
  commentsHeading: {
    borderBottomColor: theme.colors['border.secondary'],
    borderBottomWidth: 1,
    paddingBottom: theme.spacing['spacing.general.x2'],
  },
  comment: {
    paddingTop: theme.spacing['spacing.general.x1'],
  },
  commentBody: {
    lineHeight: 21,
  },
  emptyIcon: {
    backgroundColor: theme.colors['surface.secondary-neutral'],
    borderRadius: 999,
    height: 56,
    width: 56,
  },
  composer: {
    backgroundColor: theme.colors['surface.default'],
    borderTopColor: theme.colors['border.secondary'],
    borderTopWidth: 1,
    paddingBottom: theme.spacing['spacing.general.x4'],
    paddingHorizontal: theme.spacing['spacing.general.x4'],
    paddingTop: theme.spacing['spacing.general.x3'],
  },
  composerInput: {
    backgroundColor: theme.colors['surface.secondary-neutral'],
    borderColor: theme.colors['border.secondary'],
    borderRadius: 22,
    borderWidth: 1,
    flex: 1,
    fontSize: 15,
    lineHeight: 20,
    maxHeight: 112,
    minHeight: 44,
    paddingHorizontal: theme.spacing['spacing.general.x4'],
    paddingVertical: theme.spacing['spacing.general.x2'],
  },
  sendButton: {
    alignItems: 'center',
    backgroundColor: theme.colors['text.primary'],
    borderRadius: 22,
    height: 44,
    justifyContent: 'center',
    minWidth: 44,
    paddingHorizontal: theme.spacing['spacing.general.x3'],
  },
  sendButtonDisabled: {
    backgroundColor: theme.colors['surface.inverse.tertiary-neutral'],
    opacity: 0.7,
  },
});
